import React, { useMemo, useState } from "react";

// Merge timeline events (read-only) with manual log entries, sorted by time
const mergeEntries = (logEntries, timelineEvents) => {
  const manual = logEntries.map((e) => ({ ...e, source: "manual" }));

  const fromTimeline = (timelineEvents ?? [])
    .filter((e) => e.startAt)
    .map((e) => ({
      id: `event-${e.id}`,
      timestamp: e.startAt,
      description: [e.line, e.operation, e.remarks].filter(Boolean).join(" — "),
      source: "timeline",
    }));

  return [...manual, ...fromTimeline].sort(
    (a, b) => new Date(a.timestamp) - new Date(b.timestamp)
  );
};

const inlineInput =
  "w-full rounded border border-zinc-300 bg-white px-2 py-1 text-sm focus:border-navy-600 focus:outline-none focus:ring-2 focus:ring-navy-600/20";

const DetailedLogTable = ({
  logEntries,
  timelineEvents,
  dprId,
  reportDate,
  onCreate,
  onUpdate,
  onDelete,
}) => {
  const [newTime, setNewTime] = useState("");
  const [newDesc, setNewDesc] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [editTime, setEditTime] = useState("");
  const [editDesc, setEditDesc] = useState("");

  const merged = useMemo(
    () => mergeEntries(logEntries, timelineEvents),
    [logEntries, timelineEvents]
  );

  const toTimestamp = (timeStr) => {
    const [h, m] = timeStr.split(":");
    return `${reportDate}T${h.padStart(2, "0")}:${(m || "00").padStart(2, "0")}:00`;
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return "";
    const d = new Date(timestamp);
    return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", hour12: false });
  };

  const handleAdd = () => {
    if (!newTime || !newDesc.trim()) return;
    onCreate({
      dprId,
      timestamp: toTimestamp(newTime),
      description: newDesc.trim(),
    });
    setNewTime("");
    setNewDesc("");
  };

  const handleStartEdit = (entry) => {
    if (entry.source === "timeline") return;
    setEditingId(entry.id);
    setEditTime(formatTime(entry.timestamp));
    setEditDesc(entry.description);
  };

  const handleCommitEdit = () => {
    if (!editingId) return;
    onUpdate(editingId, {
      timestamp: toTimestamp(editTime),
      description: editDesc,
    });
    setEditingId(null);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") handleCommitEdit();
    if (e.key === "Escape") setEditingId(null);
  };

  return (
    <section>
      <h3 className="section-eyebrow mb-3">Log of events — last 24 hours</h3>
      <table className="table-hairline">
        <thead>
          <tr>
            <th className="w-24">Time</th>
            <th>Description</th>
            <th className="w-16 text-right" />
          </tr>
        </thead>
        <tbody>
          {merged.map((entry) => {
            const isTimeline = entry.source === "timeline";
            const isEditing = editingId === entry.id;

            return (
              <tr
                key={entry.id}
                className={isTimeline ? "text-zinc-500" : ""}
              >
                <td className="num text-zinc-700">
                  {isEditing ? (
                    <input
                      type="time"
                      value={editTime}
                      onChange={(e) => setEditTime(e.target.value)}
                      onKeyDown={handleKeyDown}
                      autoFocus
                      onFocus={(e) => e.target.select()}
                      className="w-full rounded border border-navy-600 bg-white px-1.5 py-0.5 text-sm font-mono tabular-nums focus:outline-none focus:ring-2 focus:ring-navy-600/20"
                    />
                  ) : (
                    <div
                      onClick={() => handleStartEdit(entry)}
                      className={isTimeline ? "" : "cursor-pointer"}
                    >
                      {formatTime(entry.timestamp)}
                    </div>
                  )}
                </td>
                <td>
                  {isEditing ? (
                    <input
                      type="text"
                      value={editDesc}
                      onChange={(e) => setEditDesc(e.target.value)}
                      onBlur={handleCommitEdit}
                      onKeyDown={handleKeyDown}
                      className="w-full rounded border border-navy-600 bg-white px-1.5 py-0.5 text-sm focus:outline-none focus:ring-2 focus:ring-navy-600/20"
                    />
                  ) : (
                    <div
                      onClick={() => handleStartEdit(entry)}
                      className={`min-h-[1.5em] ${
                        isTimeline ? "" : "cursor-pointer rounded px-1 -mx-1 hover:bg-navy-50/60"
                      }`}
                    >
                      {entry.description}
                      {isTimeline && (
                        <span className="ml-2 font-mono text-[10px] uppercase tracking-wide text-navy-400">
                          timeline
                        </span>
                      )}
                    </div>
                  )}
                </td>
                <td className="text-right">
                  {!isTimeline && (
                    <button
                      type="button"
                      className="text-xs font-medium text-zinc-400 hover:text-rust-600"
                      onClick={() => onDelete(entry.id)}
                    >
                      Delete
                    </button>
                  )}
                </td>
              </tr>
            );
          })}
          <tr>
            <td>
              <input
                type="time"
                value={newTime}
                onChange={(e) => setNewTime(e.target.value)}
                className={`${inlineInput} font-mono tabular-nums`}
              />
            </td>
            <td>
              <input
                type="text"
                value={newDesc}
                onChange={(e) => setNewDesc(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleAdd()}
                placeholder="Add additional detail not in timeline…"
                className={inlineInput}
              />
            </td>
            <td className="text-right">
              <button
                type="button"
                onClick={handleAdd}
                disabled={!newTime || !newDesc.trim()}
                className="text-xs font-medium text-navy-600 hover:text-navy-700 disabled:opacity-40"
              >
                Add
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </section>
  );
};

export default DetailedLogTable;
